import { MenuItem, Select, Stack, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useMobile } from "lib/@components/mobile"
import { shortFolderName } from "./build-assignment-board"

const FOLLOW = "__follow__"

/**
 * The whole schedule's trade, in the drawer header. Lets a user make one schedule an exception
 * inside an assigned folder (everything in Boilers to the gas contractor except this one)
 * without overriding each of its tasks. Setting it back follows the folder's assignment, or
 * skill routing, which the option names.
 */
export function ScheduleTradePicker({ schedule }) {
    const { board, trades, readOnly, onAssignTargets, onRemoveAssignment } = useBoundContext()
    const effective = board.scheduleAssignments.get(schedule.scheduleId)
    // Only an assignment on this schedule itself can be changed here; a folder's applies to it
    const own = effective?.kind === "schedule" ? effective : null
    const folder = board.scheduleFolderAssignments?.get(schedule.scheduleId)
    const followLabel = folder
        ? `Follow the ${shortFolderName(folder.name)} folder (${trades[folder.tradeId]})`
        : "Follow skill routing"
    const tradeOptions = Object.entries(trades).sort(byName).map(toOption)
    // As for the task pickers: below lg the drawer is a dialog, and a portalled menu closed it
    const isMobile = useMobile()

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                Whole schedule
            </Typography>
            <Select
                size="small"
                value={own?.tradeId ?? FOLLOW}
                onChange={handleChange}
                disabled={readOnly || !!own?.pending}
                sx={{ minWidth: 220, fontSize: 13 }}
                inputProps={{ "aria-label": `Trade for the whole of ${schedule.code} ${schedule.title}` }}
                MenuProps={{ disablePortal: isMobile }}
                data-cy="schedule-trade-picker"
            >
                <MenuItem value={FOLLOW}>{followLabel}</MenuItem>
                {tradeOptions}
            </Select>
        </Stack>
    )

    function handleChange(event) {
        const tradeId = event.target.value
        if (tradeId === FOLLOW) {
            if (own) onRemoveAssignment(own.id)
            return
        }
        const name = `${schedule.code} ${schedule.title}`.trim()
        onAssignTargets(tradeId, [{ kind: "schedule", targetId: schedule.scheduleId, name }], { keepSelection: true })
    }
}

// MenuItems must be direct Select children (MUI clones each one), so an array, not <Repeat>
function toOption([tradeId, tradeName]) {
    return (
        <MenuItem key={tradeId} value={tradeId}>
            {tradeName}
        </MenuItem>
    )
}

function byName([, a], [, b]) {
    return a.localeCompare(b)
}
